import styled from '@emotion/styled';
import { useTranslation } from '@hotjar/translations';
import { Stack, Text, tokens } from '@hotjar/hotsauce';
import { UnitRate } from './UnitRate';
import { MissingFeaturesList } from './MissingFeaturesList';
import { Product } from './Product';
import { CurrentPlanCard } from './CurrentPlanCard';
import { NewPlanCard } from './NewPlanCard';
import type { ChurnDeflectionAskProduct } from '../types';
import { calculateLossRate } from '../utils/calculateLossRate';
import { composePlanName } from '../utils/composePlanName';

const Wrapper = styled(Product)`
  background: radial-gradient(
      50% 75% at 10% 80%,
      rgba(156, 137, 212, 0.9) 0%,
      rgb(247, 246, 252) 100%
    )
    rgb(247, 246, 252);
`;

type Props = ChurnDeflectionAskProduct & { subscriptionEndDate: string };

export function AskProduct({ currentPlan, newPlan, subscriptionEndDate }: Props): JSX.Element {
  const { t } = useTranslation('voluntary-churn');

  const { name: currentPlanName, monthlyAllowance: currentPlanMonthlyAllowance } = currentPlan;
  const { name: newPlanName, monthlyAllowance: newPlanMonthlyAllowance } = newPlan;

  const lossRate = calculateLossRate(currentPlanMonthlyAllowance, newPlanMonthlyAllowance);

  return (
    <Wrapper>
      <Text as="h2" textType="header4">
        Ask
      </Text>
      <Stack customSpacing={tokens.spacer.x3}>
        <CurrentPlanCard
          title={composePlanName(currentPlanName, currentPlanMonthlyAllowance)}
          date={t('churn.deflection-modal.now-date')}
          testId="ask-current-plan-title"
        >
          <UnitRate
            title={t('churn.deflection-modal.maximum-responses-label')}
            numerator={currentPlanMonthlyAllowance}
            denominator={t('churn.deflection-modal.month')}
            testId="ask-current-plan-monthly-responses"
          />
        </CurrentPlanCard>
        <NewPlanCard title={newPlanName} date={subscriptionEndDate} testId="ask-new-plan-title">
          <Stack customSpacing={tokens.spacer.x3}>
            <Stack direction="column" customSpacing={tokens.spacer.x2} css={{ flex: 6 }}>
              <UnitRate
                title={t('churn.deflection-modal.maximum-responses-label')}
                numerator={newPlanMonthlyAllowance}
                denominator={t('churn.deflection-modal.month')}
                lossRate={lossRate}
                testId="ask-new-plan-monthly-responses"
              />
            </Stack>
            <Stack css={{ flex: 6 }}>
              <MissingFeaturesList
                title={t('churn.deflection-modal.missing-features-list-label')}
                testId="ask-missing-features-list"
                features={
                  currentPlanName === 'business'
                    ? [
                        'personalized-brand-colours',
                        'survey-performance',
                        'unlimited-widgets',
                        'unlimited-data-storage',
                      ]
                    : ['unlimited-widgets', 'events-api']
                }
              />
            </Stack>
          </Stack>
        </NewPlanCard>
      </Stack>
    </Wrapper>
  );
}
